import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, Loader2 } from "lucide-react";
import { api, getErrorMessage } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Toaster, toast } from "sonner";

/**
 * Self-serve password reset — step 2 of 2 (consume token + set new password).
 * Validates the token on mount so a used/expired link shows a friendly
 * explanation instead of letting the user type a password that would be
 * rejected on submit.
 */
export default function ResetPassword() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "checking", email: null, reason: null });
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState({ phase: "invalid", reason: "unknown" }); return; }
    api.get(`/auth/reset-password/${encodeURIComponent(token)}/validate`)
      .then((r) => {
        if (r.data?.valid) setState({ phase: "ready", email: r.data.email });
        else setState({ phase: "invalid", reason: r.data?.reason || "unknown" });
      })
      .catch(() => setState({ phase: "invalid", reason: "unknown" }));
  }, [token]);

  const canSubmit = pw.length >= 10 && confirmPw === pw && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      // Any previously-saved admin/buyer session on this device is orthogonal
      // to the reset; drop it so the next visit lands on the clean login flow.
      try { localStorage.removeItem("fl_token"); } catch {}
      try { localStorage.removeItem("fl_role"); } catch {}
      toast.success("Password updated — please sign in");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not reset password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white border border-[#E2E4E0] rounded-xl p-6 shadow-sm">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-[#5C665A] hover:text-[#1A1D1A] mb-4" data-testid="reset-back-to-login">
          <ChevronLeft size={12} /> Back to sign in
        </Link>
        <div className="db-h mb-5">
          <h1 className="text-2xl font-bold tracking-tight">Choose a new password</h1>
          {state.phase === "ready" && state.email && (
            <p className="text-sm text-[#5C665A] mt-1">For <span className="db-mono">{state.email}</span></p>
          )}
        </div>

        {state.phase === "checking" && (
          <div className="text-sm text-[#5C665A] inline-flex items-center gap-2" data-testid="reset-checking">
            <Loader2 size={14} className="animate-spin" /> Checking link…
          </div>
        )}

        {state.phase === "invalid" && (
          <div
            data-testid="reset-invalid"
            className="rounded-md border border-[#F2C7C7] bg-[#FDF3F3] p-4 text-sm text-[#8A1A1A]"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#C94A4A]" />
              <div>
                <div className="font-medium" data-testid="reset-invalid-reason">
                  {state.reason === "expired" && "This reset link has expired."}
                  {state.reason === "used" && "This reset link has already been used."}
                  {state.reason === "unknown" && "This reset link isn't valid."}
                </div>
                <p className="text-[#C94A4A] mt-1 leading-relaxed">
                  Reset links are good for 60 minutes and can only be used once.
                  You can request a new one from the sign-in page.
                </p>
                <div className="mt-3 flex gap-2 text-xs">
                  <Link to="/forgot-password" className="underline underline-offset-2" data-testid="reset-request-new">
                    Request a new link
                  </Link>
                  <span className="text-[#8A9088]">·</span>
                  <Link to="/login" className="underline underline-offset-2" data-testid="reset-return-login">
                    Back to sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {state.phase === "ready" && (
          <form onSubmit={submit} data-testid="reset-form" className="space-y-3">
            <div>
              <Label className="text-xs text-[#5C665A]">New password <span className="text-[#8A9088]">(min 10 chars)</span></Label>
              <Input
                data-testid="reset-new-password"
                type="password"
                required
                autoFocus
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="h-10"
              />
              {pw.length > 0 && pw.length < 10 && (
                <div className="text-[11px] text-[#C94A4A] mt-1">Too short — at least 10 characters.</div>
              )}
            </div>
            <div>
              <Label className="text-xs text-[#5C665A]">Confirm new password</Label>
              <Input
                data-testid="reset-confirm-password"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="h-10"
              />
              {confirmPw.length > 0 && confirmPw !== pw && (
                <div className="text-[11px] text-[#C94A4A] mt-1">Does not match the new password.</div>
              )}
            </div>
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="reset-submit"
              className="w-full h-10 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50"
            >
              {busy ? <><Loader2 className="animate-spin" size={14} /> Updating…</> : "Set new password"}
            </Button>
          </form>
        )}
      </div>
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
